import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import type { UsSport } from "@/lib/usSports";
import { cn } from "@/lib/utils";

interface Props {
  sport: UsSport;
  /** Current surface, so it renders as plain text instead of a self-link. */
  current?: string;
  className?: string;
}

/**
 * The internal-link spine every non-football sport was missing.
 *
 * Football pages link out to fixtures, today/tomorrow landings, team hubs and
 * guides; other sports only had a hub plus market pages, which left their match
 * pages nearly orphaned. This block links every sport surface to every other,
 * on every sport page, so crawlers reach them all from any entry point.
 */
export function SportHubLinks({ sport, current, className }: Props) {
  const base = `/${sport.slug}-predictions`;
  const items: { key: string; label: string; path: string }[] = [
    { key: "hub", label: `${sport.label} hub`, path: base },
    { key: "today", label: "Today", path: `${base}/today` },
    { key: "tomorrow", label: "Tomorrow", path: `${base}/tomorrow` },
    { key: "weekend", label: "Weekend", path: `${base}/weekend` },
    { key: "fixtures", label: "All fixtures", path: `${base}/fixtures` },
    { key: "teams", label: "Teams", path: `${base}/teams` },
    ...sport.markets.map((m) => ({
      key: `market-${m.slug}`,
      label: m.label,
      path: `${base}/${m.slug}`,
    })),
  ];

  return (
    <nav
      aria-label={uiText("{{v0}} predictions sections", { v0: sport.label })}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {items.map((i) =>
        i.key === current ? (
          <span
            key={i.key}
            aria-current="page"
            className="rounded-full border border-primary/50 bg-primary/10 px-3 py-1 text-xs font-medium"
          >
            {uiText(i.label)}
          </span>
        ) : (
          <Link
            key={i.key}
            to={i.path}
            className="rounded-full border px-3 py-1 text-xs hover:bg-muted transition-colors"
          >
            {uiText(i.label)}
          </Link>
        ),
      )}
    </nav>
  );
}
